class CartAddedPopup extends HTMLElement {
  constructor() {
    super();

    this.popup = document.getElementById('cart-added-popup');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.shippingThresholds = this.parseShippingThresholds();

    this.popup.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    this.popup.querySelector('.cart-added-popup__close')
      ?.addEventListener('click', this.close.bind(this));

    this.popup.querySelector('.cart-added-popup__view-cart')
      ?.addEventListener('click', async () => {
        this.close();
        if (typeof window.refreshCartDrawerUpsell === 'function') {
          await window.refreshCartDrawerUpsell();
        }
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.open();
      });
  }

  open() {
    this.popup.setAttribute('aria-hidden', 'false');
    this.popup.classList.add('active');

    this.popup.addEventListener(
      'transitionend',
      () => {
        this.popup.focus();
        trapFocus(this.popup);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = setTimeout(() => this.close(), 5000);
  }

  close() {
    clearTimeout(this.autoCloseTimer);
    this.popup.classList.remove('active');
    this.popup.setAttribute('aria-hidden', 'true');
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  renderContents(parsedState) {
    // /cart/add.js returns either a single item object (FormData POST)
    // or { items: [...], sections: {...} } (JSON POST with items array).
    // Kaching Bundles adds several items at once (sets + free gifts like Button Cover Pin),
    // so pick the primary purchased item — not the free gift.
    const item = this.pickDisplayItem(parsedState);
    this.populateItemData(item);

    // Update cart icon bubble count
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble && parsedState.sections?.['cart-icon-bubble']) {
      cartIconBubble.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble']);
    }

    // Pre-render cart drawer HTML so it's ready when "View Cart" is clicked
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && parsedState.sections?.['cart-drawer']) {
      const cartDrawerEl = document.getElementById('CartDrawer');
      if (cartDrawerEl) {
        cartDrawerEl.innerHTML = this.getSectionInnerHTML(
          parsedState.sections['cart-drawer'],
          '#CartDrawer'
        );
        cartDrawer.classList.remove('is-empty');
        cartDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
        cartDrawer.querySelector('cart-drawer-items')?.classList.remove('is-empty');
        const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
      }
    }

    if (typeof window.refreshCartDrawerUpsell === 'function') {
      window.refreshCartDrawerUpsell();
    }

    this.updateShippingProgress();
    this.open();
  }

  pickDisplayItem(parsedState) {
    const items = Array.isArray(parsedState?.items) && parsedState.items.length
      ? parsedState.items
      : parsedState
        ? [parsedState]
        : [];

    if (!items.length) return parsedState;

    const linePrice = (item) =>
      Number(item.final_line_price ?? item.line_price ?? item.final_price ?? item.price ?? 0);

    const isLikelyGift = (item) => {
      const props = item.properties || {};
      const propValues = Object.values(props).map((v) => String(v).toLowerCase());
      const propKeys = Object.keys(props).map((k) => k.toLowerCase());
      const title = String(item.product_title || item.title || '').toLowerCase();
      const handle = String(item.handle || '').toLowerCase();

      if (linePrice(item) <= 0) return true;
      if (propKeys.some((k) => k.includes('gift') || k.startsWith('_gift'))) return true;
      if (propValues.some((v) => v === 'true' && propKeys.some((k) => k.includes('gift')))) return true;
      if (title.includes('cover pin') || handle.includes('cover-pin')) return true;
      return false;
    };

    const paidItems = items.filter((item) => !isLikelyGift(item));
    const candidates = paidItems.length ? paidItems : items;

    const pageProductId =
      window.meta?.product?.id ||
      window.ShopifyAnalytics?.meta?.product?.id ||
      document.querySelector('product-info[data-product-id]')?.dataset?.productId ||
      document.querySelector('[data-product-id]')?.dataset?.productId;

    if (pageProductId) {
      const match = candidates.find(
        (item) => Number(item.product_id) === Number(pageProductId)
      );
      if (match) return match;
    }

    return candidates[0];
  }

  populateItemData(item) {
    if (!item) return;
    const imageEl = document.getElementById('cart-added-popup-image');
    const titleEl = document.getElementById('cart-added-popup-title');
    const variantEl = document.getElementById('cart-added-popup-variant');
    const qtyEl = document.getElementById('cart-added-popup-qty');
    const priceEl = document.getElementById('cart-added-popup-price');

    if (imageEl && item.featured_image?.url) {
      imageEl.src = item.featured_image.url;
      imageEl.alt = item.featured_image.alt || item.title || '';
    }

    if (titleEl) titleEl.textContent = item.title || '';

    if (variantEl) {
      variantEl.textContent = item.variant_title || '';
      variantEl.hidden = !item.variant_title;
    }

    if (qtyEl) qtyEl.textContent = item.quantity ?? '';

    if (priceEl) {
      const cents = item.final_line_price ?? item.line_price ?? item.price ?? 0;
      if (window.Shopify?.formatMoney) {
        const formatted = window.Shopify.formatMoney(
          cents,
          window.Shopify.money_format
        );
        // Theme / apps (e.g. glc-money) often return HTML spans; textContent would show literal tags
        if (String(formatted).indexOf('<') !== -1) {
          priceEl.innerHTML = formatted;
        } else {
          priceEl.textContent = formatted;
        }
      } else {
        priceEl.textContent = (cents / 100).toFixed(2);
      }
    }
  }

  parseShippingThresholds() {
    try {
      const raw = this.getAttribute('data-thresholds');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  getShippingThreshold(currency) {
    const code = String(currency || '').toUpperCase();
    const thresholds = this.shippingThresholds || {};
    const exact = Number(thresholds[code]);
    if (Number.isFinite(exact) && exact > 0) return exact;

    const fallback = Number(thresholds.DEFAULT);
    if (Number.isFinite(fallback) && fallback > 0) return fallback;

    return null;
  }

  formatMoney(cents) {
    try {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format);
      }
    } catch (_) {}
    return (cents / 100).toFixed(2);
  }

  formatMoneyText(cents) {
    const formatted = String(this.formatMoney(cents));
    if (formatted.indexOf('<') === -1) return formatted;
    const doc = new DOMParser().parseFromString(formatted, 'text/html');
    return doc.body.textContent || formatted;
  }

  updateShippingProgress() {
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        const shippingEl = document.getElementById('cart-added-popup-shipping');
        const progressEl = document.getElementById('cart-added-popup-progress');
        const textEl = document.getElementById('cart-added-popup-shipping-text');

        if (!shippingEl) return;

        const threshold = this.getShippingThreshold(cart.currency);

        // No admin cutoff for this currency — hide progress rather than guessing.
        if (!threshold) {
          shippingEl.hidden = true;
          return;
        }

        const totalPrice = Number(cart.total_price) || 0;
        const remaining = threshold - totalPrice;

        shippingEl.hidden = false;

        if (remaining <= 0) {
          shippingEl.classList.add('cart-added-popup__shipping--achieved');
          if (progressEl) progressEl.style.width = '100%';
          if (textEl) textEl.textContent = shippingEl.dataset.achievedLabel || "You've unlocked free shipping!";
          return;
        }

        shippingEl.classList.remove('cart-added-popup__shipping--achieved');

        if (progressEl) {
          const pct = Math.min((totalPrice / threshold) * 100, 100);
          progressEl.style.width = `${pct}%`;
        }

        if (textEl) {
          textEl.textContent = `${this.formatMoneyText(Math.abs(remaining))} away from free shipping`;
        }
      })
      .catch((err) => {
        console.error('[cart-popup] shipping fetch error:', err);
      });
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.popup && !target.closest('cart-added-popup')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }
}

customElements.define('cart-added-popup', CartAddedPopup);
